import { combineReducers } from "redux";
import * as types from "./types";
import * as searchTypes from "../search/types";
import { argumentString } from "./utils";
import { USER_SIGNED_OUT } from "../user/types";

const bridgeUserReducer = (state = {}, action) => {
  switch (action.type) {
    case types.FETCH_SESSION_REQUEST:
      // Prevent extra dispatches from clearing the session state
      if (!state.status) {
        return { isFetching: true };
      }
      return state;
    case types.FETCH_SESSION_SUCCESS:
    case types.SYNC_FETCH_SESSION_SUCCESS: {
      const credits =
        action.response.creditList &&
        action.response.creditList.reduce((acc, credit) => {
          acc[credit.id] = credit;
          return acc;
        }, {});
      return {
        ...action.response,
        creditList: credits,
        isFetching: false
      };
    }
    case types.FETCH_SESSION_FAILURE:
      return { ...state, isFetching: false };
    case USER_SIGNED_OUT:
      return {};
    default:
      return state;
  }
};

const promotionReducer = (state = {}, action) => {
  switch (action.type) {
    case types.FETCH_SESSION_REQUEST:
      return { isFetching: true, ...state };
    case types.FETCH_SESSION_SUCCESS:
      return {
        isFetching: false,
        promotions:
          action.response.promotions &&
          action.response.promotions.reduce((acc, promo) => {
            acc[promo.value] = promo;
            return acc;
          }, {})
      };
    case types.FETCH_SESSION_FAILURE:
      return { ...state, isFetching: false };
    case types.ACTIVATE_PROMOTION_REQUEST:
      return {
        promotions: {
          ...state.promotions,
          [action.vbid]: {
            ...state[action.vbid],
            active: true
          }
        },
        isFetching: true
      };
    case types.ACTIVATE_PROMOTION_SUCCESS:
      return {
        promotions: {
          ...state.promotions,
          [action.vbid]: {
            ...state[action.vbid],
            active: true
          }
        }
      };
    case types.DEACTIVATE_PROMOTION_REQUEST:
      return {
        promotions: {
          ...state.promotions,
          [action.vbid]: {
            ...state.promotions[action.vbid],
            active: false
          }
        },
        isFetching: true
      };
    case types.DEACTIVATE_PROMOTION_SUCCESS:
      return {
        promotions: {
          ...state.promotions,
          [action.vbid]: {
            ...state[action.vbid],
            active: false
          }
        },
        isFetching: false
      };
    case types.ACTIVATE_PROMOTION_FAILURE:
    case types.DEACTIVATE_PROMOTION_FAILURE:
      return { ...state, isFetching: false };
    default:
      return state;
  }
};

const assetReducerPlugin = (state, action) => {
  switch (action.type) {
    case searchTypes.SEARCH_BOOK_METADATA_SUCCESS:
      if (action.source === searchTypes.BRIDGE_SOURCE) {
        const searchByVBID = action.results.reduce((acc, book) => {
          acc[book.vbid] = book;
          return acc;
        }, {});
        return { ...state, byId: { ...state.byId, ...searchByVBID } };
      } else {
        return state;
      }
    case types.ACTIVATE_ASSET_REQUEST:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: { ...state.byId[action.vbid], activating: true }
        }
      };
    case types.ACTIVATE_ASSET_SUCCESS:
    case types.SYNC_ACTIVATE_ASSET_SUCCESS:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: {
            ...action.asset,
            ...state.byId[action.vbid],
            activating: false,
            active: true
          }
        }
      };
    case types.ACTIVATE_ASSET_FAILURE:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: {
            ...state.byId[action.vbid],
            activating: false
          }
        }
      };
    case types.RETURN_ASSET_REQUEST:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: { ...state.byId[action.vbid], returning: true }
        }
      };
    case types.RETURN_ASSET_SUCCESS:
    case types.SYNC_RETURN_ASSET_SUCCESS: {
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: action.asset
        }
      };
    }
    case types.RETURN_ASSET_FAILURE:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: {
            ...state.byId[action.vbid],
            returning: false
          }
        }
      };
    case types.FETCH_SINGLE_ASSET_REQUEST:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: {
            ...(state.byId[action.vbid] || {}),
            isFetching: true
          }
        }
      };
    case types.FETCH_SINGLE_ASSET_SUCCESS:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.asset.vbid]: {
            ...action.asset,
            isFetching: false
          }
        }
      };
    case types.FETCH_SINGLE_ASSET_FAILURE:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.vbid]: {
            ...(state.byId[action.vbid] || {}),
            isFetching: false
          }
        }
      };

    case USER_SIGNED_OUT:
      return undefined;
    default:
      return state;
  }
};

const genericReducer =
  (request, success, failure, plugin) =>
  (state = { results: {}, byId: {} }, action) => {
    switch (action.type) {
      case USER_SIGNED_OUT:
        return { results: {}, byId: {} };
      case request:
        const requestArgString = argumentString(action.args);
        const requestPage = action.args.page || 0;
        const requestResults = state.results[requestArgString] || {};
        return {
          ...state,
          results: {
            ...state.results,
            [requestArgString]: {
              ...requestResults,
              pages: {
                ...(requestResults.pages || {}),
                [requestPage]: { isFetching: true }
              }
            }
          }
        };
      case success:
        const successArgString = argumentString(action.args);
        const successPage = action.args.page || 0;
        const successResults = state.results[successArgString] || {};
        return {
          ...state,
          byId: { ...state.byId, ...action.data.entities.content },
          results: {
            ...state.results,
            [successArgString]: {
              ...successResults,
              pages: {
                ...(successResults.pages || {}),
                [successPage]: {
                  hasFetched: true,
                  isFetching: false,
                  content: action.data.result
                }
              },
              ...action.metadata
            }
          }
        };
      case failure:
        return { ...state, isFetching: false, error: action.error };
      default:
        return plugin ? plugin(state, action) : state;
    }
  };

const ancillariesByVbidReducer = (state = {}, action) => {
  switch (action.type) {
    case types.FETCH_BRIDGE_ANCILLARIES_REQUEST:
      return { ...state, [action.isbn]: { isFetching: true } };
    case types.FETCH_BRIDGE_ANCILLARIES_SUCCESS:
      return {
        ...state,
        [action.isbn]: {
          title: action.data.title,
          ancillaries: action.data.ancillaries,
          isFetching: false
        }
      };
    case types.FETCH_BRIDGE_ANCILLARIES_FAILURE:
      return {
        ...state,
        [action.isbn]: {
          title: "error",
          ancillaries: [],
          isFetching: false
        }
      };
    case USER_SIGNED_OUT:
      return {};
    default:
      return state;
  }
};

const bookStoreReducer = (state = {}, action) => {
  switch (action.type) {
    case types.SEARCH_BOOK_STORE_REQUEST:
      return {
        ...state,
        isSearching: true,
        query: action.query,
        results: []
      };
    case types.SEARCH_BOOK_STORE_SUCCESS:
      return {
        ...state,
        query: action.query,
        results: action.results
      };
    case types.SEARCH_BOOK_STORE_COMPLETE:
      return {
        ...state,
        query: action.query,
        isSearching: false
      };
    case types.SEARCH_BOOK_STORE_FAILURE:
      return {
        ...state,
        isSearching: false,
        results: []
      };
    default:
      return state;
  }
};

const bridgeReducer = combineReducers({
  groups: genericReducer(
    types.FETCH_GROUPS_REQUEST,
    types.FETCH_GROUPS_SUCCESS,
    types.FETCH_GROUPS_FAILURE
  ),
  adoptions: genericReducer(
    types.FETCH_ADOPTIONS_REQUEST,
    types.FETCH_ADOPTIONS_SUCCESS,
    types.FETCH_ADOPTIONS_FAILURE
  ),
  assets: genericReducer(
    types.FETCH_ASSETS_REQUEST,
    types.FETCH_ASSETS_SUCCESS,
    types.FETCH_ASSETS_FAILURE,
    assetReducerPlugin
  ),
  ancillaries: ancillariesByVbidReducer,
  user: bridgeUserReducer,
  promotions: promotionReducer,
  bookStore: bookStoreReducer
});

export default bridgeReducer;
